import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import storeService from '../services/storeService';
import { FiPlusCircle, FiEdit, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const StoresPage = () => {
  const [stores, setStores] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canCreateStore = permissions.includes('stores:create');
  const canUpdateStore = permissions.includes('stores:update');
  const canDeleteStore = permissions.includes('stores:delete');
  const canManageAnyStore = canUpdateStore || canDeleteStore;

  useEffect(() => {
    storeService.getStores().then(setStores).catch(() => setError('Failed to fetch stores.'));
  }, []);

  const handleDeleteStore = async (id) => {
    if (window.confirm('Are you sure? This will deactivate the store.')) {
      try {
        await storeService.deleteStore(id);
        storeService.getStores().then(setStores);
      } catch (err) {
        setError('Failed to deactivate store.');
      }
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Store Management</h1>
        {canCreateStore && (
          <button onClick={() => navigate('/stores/settings/new')} className="flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark">
            <FiPlusCircle className="mr-2" /> Add New Store
          </button>
        )}
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stores.map(store => (
              <tr key={store._id}>
                <td className="px-6 py-4">{store.name}</td>
                <td className="px-6 py-4">{store.type}</td>
                <td className="px-6 py-4">{store.address ? `${store.address.street || ''}, ${store.address.suburb || ''}, ${store.address.state || ''} ${store.address.postcode || ''}`.replace(/ ,/g, '').trim().replace(/^,|,$/g, '') : 'N/A'}</td>
                <td className="px-6 py-4"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${store.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{store.isActive ? 'Active' : 'Inactive'}</span></td>
                <td className="px-6 py-4 text-right space-x-4">
                  {canUpdateStore && (
                    <button onClick={() => navigate(`/stores/settings/${store._id}`)} className="text-indigo-600 hover:text-indigo-900" title="Edit Settings"><FiEdit size={18} /></button>
                  )}
                  {canDeleteStore && (
                    <button onClick={() => handleDeleteStore(store._id)} className="text-red-600 hover:text-red-900" title="Deactivate Store"><FiTrash2 size={18} /></button>
                  )}
                  {!canManageAnyStore && (
                    <span className="text-sm text-gray-400">View only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StoresPage;
